// Reference EnhancedSingleSelectAdapter backed by downshift's useSelect hook —
// a custom dropdown replacing the native <select>, so the open menu can be
// styled (the native popup ignores page CSS entirely). Shipped from the same
// `@scientist/dynamic-form-renderer/enhanced-select` entry as the multi-select
// so hosts that don't use it never pull downshift into the core bundle.
//
// downshift owns the combobox ARIA wiring and keyboard navigation; this module
// only renders Bootstrap 5 markup. The toggle is a `.form-select`-classed DIV
// (not a native <button> — legacy host stylesheets style bare button elements
// with press states that fight the input look); it inherits the host's input
// styling with zero extra CSS. Values stay JSON-typed throughout.

import { useSelect } from 'downshift'
import type { EnhancedSingleSelectProps } from '../rendererContext'
import type { Option } from '../types'
import { optionKey, valuesEqual } from '../fields/optionUtils'

/** Sentinel item for the clearable "none" choice (maps to value undefined). */
const NONE_ITEM: unique symbol = Symbol('none')
type MenuItem = Option | typeof NONE_ITEM

export function DownshiftSingleSelect({
  id,
  options,
  value,
  disabled,
  noneLabel,
  hasErrors,
  ariaInvalid,
  ariaRequired,
  ariaDescribedBy,
  classNames,
  onChange,
  onBlur,
}: EnhancedSingleSelectProps) {
  const items: MenuItem[] = noneLabel !== undefined ? [NONE_ITEM, ...options] : [...options]
  const selectedOption = options.find((opt) => valuesEqual(opt.value, value)) ?? null

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect<MenuItem>({
    id,
    items,
    itemToString: (item) => (item == null || item === NONE_ITEM ? '' : item.label),
    selectedItem: selectedOption,
    onSelectedItemChange({ selectedItem }) {
      onChange(selectedItem === NONE_ITEM || selectedItem == null ? undefined : selectedItem.value)
      onBlur?.()
    },
  })

  const toggleClass = [
    classNames.select ?? 'form-select',
    'text-start text-truncate',
    hasErrors ? 'is-invalid' : '',
    disabled ? 'pe-none bg-light' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="position-relative">
      <div
        className={toggleClass}
        aria-disabled={disabled || undefined}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        {...getToggleButtonProps({
          id,
          'aria-describedby': ariaDescribedBy,
          onBlur,
          disabled,
          tabIndex: disabled ? -1 : 0,
        })}
      >
        {selectedOption ? (
          selectedOption.label
        ) : (
          <span className="text-muted">{noneLabel || ' '}</span>
        )}
      </div>
      <ul
        className={[
          classNames.enhancedSelectMenu ??
            'dropdown-menu w-100 mt-1 p-2 border rounded-3 shadow dfr-select-menu',
          isOpen ? 'show' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ maxHeight: '16rem', overflowY: 'auto' }}
        {...getMenuProps()}
      >
        {isOpen
          ? items.map((item, index) => {
              const isNone = item === NONE_ITEM
              const isSelected = !isNone && selectedOption != null && valuesEqual(item.value, selectedOption.value)
              return (
                <li
                  key={isNone ? '__none__' : optionKey(item.value)}
                  className={[
                    classNames.enhancedSelectOption ?? 'dropdown-item rounded-2 py-2 px-2',
                    index === highlightedIndex
                      ? (classNames.enhancedSelectOptionHighlighted ?? 'active')
                      : '',
                    isSelected ? 'fw-semibold' : '',
                    isNone ? 'text-muted' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ cursor: 'pointer' }}
                  {...getItemProps({ item, index })}
                >
                  {isNone ? noneLabel || '—' : item.label}
                </li>
              )
            })
          : null}
      </ul>
    </div>
  )
}
